<section
  aria-labelledby="two-factor-title"
  class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
>
  <h2
    id="two-factor-title"
    class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
  >
    <span aria-hidden="true">🔐</span> Double authentification
  </h2>

  @if (error()) {
    <p
      role="alert"
      class="mb-4 rounded-lg px-4 py-3 text-sm bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300"
    >
      {{ error() }}
    </p>
  }

  @switch (step()) {
    @case ("setup") {
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Scannez ce QR code avec votre application d'authentification (Google
        Authenticator, Authy…), puis saisissez le code à 6 chiffres affiché.
      </p>
      @if (qrCode()) {
        <img
          [src]="qrCode()"
          alt="QR code de configuration de la double authentification"
          class="w-48 h-48 mx-auto mb-4"
        />
      } @else {
        <p class="text-sm text-gray-500 mb-4">Chargement…</p>
      }
      @if (secret()) {
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
          Ou saisissez cette clé dans l'application :
          <code class="font-mono break-all">{{ secret() }}</code>
        </p>
      }
      <form
        [formGroup]="setupForm"
        (ngSubmit)="confirmSetup()"
        class="flex flex-col sm:flex-row gap-3 items-end"
      >
        <label class="text-sm grow">
          <span class="block mb-1 text-gray-700 dark:text-gray-300"
            >Code à 6 chiffres</span
          >
          <input
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            formControlName="code"
            class="w-full rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-900 px-3 py-2 tracking-widest"
          />
        </label>
        <button
          type="submit"
          [disabled]="setupForm.invalid || busy()"
          class="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
        >
          Vérifier et activer
        </button>
        <button
          type="button"
          (click)="close()"
          class="px-5 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg"
        >
          Annuler
        </button>
      </form>
    }

    @case ("backup") {
      <p
        role="status"
        class="mb-4 rounded-lg px-4 py-3 text-sm bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
      >
        Double authentification activée. Conservez ces codes de secours en lieu
        sûr : chacun permet une seule connexion si vous perdez votre téléphone.
        Ils ne seront plus affichés.
      </p>
      <ul class="grid grid-cols-2 gap-2 mb-4" aria-label="Codes de secours">
        @for (code of backupCodes(); track code) {
          <li
            class="font-mono text-sm text-center py-1 bg-gray-100 dark:bg-slate-700 rounded"
          >
            {{ code }}
          </li>
        }
      </ul>
      <div class="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          (click)="copyBackupCodes()"
          class="px-5 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg"
        >
          {{ copied() ? "Codes copiés" : "Copier les codes" }}
        </button>
        <button
          type="button"
          (click)="close()"
          class="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
        >
          J'ai noté mes codes
        </button>
      </div>
    }

    @case ("disable") {
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Pour désactiver la double authentification, confirmez avec votre mot de
        passe et un code de votre application (ou un code de secours).
      </p>
      <form
        [formGroup]="disableForm"
        (ngSubmit)="confirmDisable()"
        class="grid gap-3 sm:grid-cols-2 items-end"
      >
        <label class="text-sm">
          <span class="block mb-1 text-gray-700 dark:text-gray-300"
            >Mot de passe</span
          >
          <input
            type="password"
            autocomplete="current-password"
            formControlName="password"
            class="w-full rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-900 px-3 py-2"
          />
        </label>
        <label class="text-sm">
          <span class="block mb-1 text-gray-700 dark:text-gray-300">Code</span>
          <input
            type="text"
            autocomplete="one-time-code"
            maxlength="20"
            formControlName="code"
            class="w-full rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-900 px-3 py-2"
          />
        </label>
        <div class="sm:col-span-2 flex gap-3">
          <button
            type="submit"
            [disabled]="disableForm.invalid || busy()"
            class="px-5 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
          >
            Désactiver
          </button>
          <button
            type="button"
            (click)="close()"
            class="px-5 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg"
          >
            Annuler
          </button>
        </div>
      </form>
    }

    @default {
      <div
        class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <p class="text-sm text-gray-600 dark:text-gray-400">
          @if (enabled() === null) {
            Chargement…
          } @else if (enabled()) {
            Activée : un code de votre application vous est demandé à chaque
            connexion.
          } @else {
            Désactivée. Activez-la pour demander un code de votre téléphone en
            plus du mot de passe à chaque connexion.
          }
        </p>
        @if (enabled() === true) {
          <button
            type="button"
            (click)="startDisable()"
            class="px-5 py-2.5 border border-red-300 text-red-700 rounded-lg font-semibold whitespace-nowrap"
          >
            Désactiver
          </button>
        } @else if (enabled() === false) {
          <button
            type="button"
            (click)="startSetup()"
            [disabled]="busy()"
            class="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 whitespace-nowrap disabled:opacity-50"
          >
            Activer
          </button>
        }
      </div>
    }
  }
</section>
